import type { Command, CommandContext, CommandResult } from "./Command";

export class TransactionCommand implements Command {
  readonly id = "transaction";

  constructor(
    readonly label: string,
    private readonly commands: Command[],
  ) {}

  execute(context: CommandContext): CommandResult {
    const results = this.commands.map(command => command.execute(context));
    return mergeResults(results);
  }

  undo(context: CommandContext): CommandResult {
    const results = [...this.commands].reverse().map(command => command.undo(context));
    return mergeResults(results);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof TransactionCommand
      && command.label === this.label
      && command.commands.length === this.commands.length
      && this.commands.every((entry, index) => {
        const next = command.commands[index];
        return Boolean(next && entry.canMergeWith?.(next) && entry.merge);
      });
  }

  merge(command: Command): Command {
    if (!(command instanceof TransactionCommand) || !this.canMergeWith(command)) {
      return this;
    }

    return new TransactionCommand(
      this.label,
      this.commands.map((entry, index) => entry.merge!(command.commands[index]!)),
    );
  }
}

function mergeResults(results: CommandResult[]): CommandResult {
  return {
    changedNodeIds: [...new Set(results.flatMap(result => result.changedNodeIds))],
    affectedBounds: results.flatMap(result => result.affectedBounds),
    previewCompatible: results.length > 0 && results.every(result => result.previewCompatible === true),
  };
}
